import { Time } from '@morpho-org/morpho-ts'
import { getUnixTime } from 'date-fns'
import { isEqual } from 'lodash-es'

import type { HistoryRange } from '@/lib/transforms/history/window'

import {
  TimeseriesInterval,
  type TimeseriesOptions,
  VaultV1LookbackPeriod,
  VaultV2LookbackPeriod
} from '@/types/enums'

export enum Timespan {
  OneDay = '1d',
  OneWeek = '1w',
  OneMonth = '1m',
  ThreeMonths = '3m',
  OneYear = '1y',
  YearToDay = 'ytd',
  AllTime = 'all'
}

/**
 * Converts a Timespan enum to a display label.
 * @param timespan - Timespan enum value
 * @returns Human-readable label (e.g., "1D", "1W", "All time")
 * @internal
 */
export function getTimespanLabel(timespan: Timespan) {
  switch (timespan) {
    case Timespan.OneDay:
      return '1D'
    case Timespan.OneWeek:
      return '1W'
    case Timespan.OneMonth:
      return '1M'
    case Timespan.ThreeMonths:
      return '3M'
    case Timespan.OneYear:
      return '1Y'
    case Timespan.YearToDay:
      return 'Year to day'
    case Timespan.AllTime:
      return 'All time'
  }
}

/**
 * Maps a Timespan enum to its corresponding data identifier key.
 * @param timespan - Timespan enum value
 * @returns Data key string (e.g., "dailyApys", "weeklyApys")
 * @internal
 */
export function getTimespanDataIdentifier(timespan: Timespan) {
  switch (timespan) {
    case Timespan.OneDay:
      return 'dailyApys'
    case Timespan.OneWeek:
      return 'weeklyApys'
    case Timespan.OneMonth:
      return 'monthlyApys'
    case Timespan.ThreeMonths:
      return 'quarterlyApys'
    case Timespan.OneYear:
      return 'yearlyApys'
    case Timespan.AllTime:
      return 'allTimeApys'
  }
}

/**
 * Get the VaultV1 lookback period from a time frame
 *
 * @param {Time.PeriodLike | null} timeFrame - time frame to get the lookback period from
 * @internal
 */
export function getV1LookbackPeriodFromTimeFrame(
  timeFrame: Time.PeriodLike | null
): VaultV1LookbackPeriod {
  if (timeFrame === null) {
    return VaultV1LookbackPeriod.Inception
  }
  if (timeFrame === 'mo') {
    return VaultV1LookbackPeriod.ThirtyDays
  }
  if (isEqual(timeFrame, { unit: 'mo', duration: 3 })) {
    return VaultV1LookbackPeriod.NinetyDays
  }
  if (timeFrame === 'w') {
    return VaultV1LookbackPeriod.SevenDays
  }
  return VaultV1LookbackPeriod.SixHours
}

/**
 * Get the VaultV2 lookback period from a time frame
 *
 * @param {Time.PeriodLike | null} timeFrame - time frame to get the lookback period from
 * @internal
 */
export function getLookbackPeriodFromTimeFrame(
  timeFrame: Time.PeriodLike | null
): VaultV2LookbackPeriod {
  if (timeFrame === null) {
    return VaultV2LookbackPeriod.Inception
  }
  if (timeFrame === 'mo') {
    return VaultV2LookbackPeriod.ThirtyDays
  }
  if (isEqual(timeFrame, { unit: 'mo', duration: 3 })) {
    return VaultV2LookbackPeriod.NinetyDays
  }
  if (timeFrame === 'w') {
    return VaultV2LookbackPeriod.SevenDays
  }
  return VaultV2LookbackPeriod.SixHours
}

/**
 * Get the oRPC history range from a time frame
 *
 * @param {Time.PeriodLike | null} timeFrame - time frame to get the history range from
 */
export function getHistoryRangeFromTimeFrame(timeFrame: Time.PeriodLike | null): HistoryRange {
  if (timeFrame === null) {
    return 'all'
  }
  if (timeFrame === 'mo') {
    return '1m'
  }
  if (isEqual(timeFrame, { unit: 'mo', duration: 3 })) {
    return '3m'
  }
  return '1w'
}

/**
 * Converts a Timespan enum to TimeseriesOptions with appropriate start/end timestamps and interval.
 * @param timespan - Timespan enum value
 * @returns TimeseriesOptions with startTimestamp, endTimestamp, and interval
 * @internal
 */
export function getTimeseriesOptions(timespan: Timespan): TimeseriesOptions {
  const now = new Date()
  const endTimestamp = getUnixTime(now)
  let startTimestamp = getUnixTime(now)
  let interval
  switch (timespan) {
    case Timespan.OneDay:
      startTimestamp -= 24 * 60 * 60
      interval = TimeseriesInterval.FifteenMinutes
      break
    case Timespan.OneWeek:
      startTimestamp -= 7 * 24 * 60 * 60
      interval = TimeseriesInterval.HalfHour
      break
    case Timespan.OneMonth:
      startTimestamp -= 30 * 24 * 60 * 60
      interval = TimeseriesInterval.Hour
      break
    case Timespan.OneYear:
      startTimestamp -= 365 * 24 * 60 * 60
      interval = TimeseriesInterval.Day
      break
    case Timespan.YearToDay:
      startTimestamp = getUnixTime(new Date(now.getFullYear(), 0, 0))
      interval = TimeseriesInterval.Day
      break
    case Timespan.AllTime:
      startTimestamp = 0
      interval = TimeseriesInterval.Day
      break
    case Timespan.ThreeMonths:
    default:
      startTimestamp -= 90 * 24 * 60 * 60
      interval = TimeseriesInterval.Day
      break
  }

  return { startTimestamp, endTimestamp, interval }
}

/**
 * Determines the appropriate timeseries interval for a given time frame.
 * @param timeFrame - Time period specification
 * @returns TimeseriesInterval (Hour for weekly, Day otherwise)
 */
export function getIntervalFromTimeFrame(timeFrame: Time.PeriodLike | null) {
  switch (timeFrame) {
    case 'w':
      return TimeseriesInterval.Hour
    default:
      return TimeseriesInterval.Day
  }
}

/**
 *  Compute the next hour timestamp. Used as a startTimestamp in the historical data queries.
 */
export function ceilOfHour(timestamp = Number(Time.timestamp())) {
  return Math.ceil(timestamp / 3600) * 3600
}

/**
 * Get timeseries options from a timeframe and a now
 *
 * @param timeframe - timeframe to get the options from
 * @param now - now to get the options from
 * @internal
 */
export function getTimeseriesOptionsFromTimeframe({
  timeFrame,
  now
}: {
  timeFrame: Time.PeriodLike | null
  now: number
}): TimeseriesOptions {
  return {
    startTimestamp: timeFrame ? now - Time.s.fromPeriod(timeFrame) : 0,
    endTimestamp: now,
    interval: getIntervalFromTimeFrame(timeFrame)
  }
}

/** A chart timeframe as a history procedure's range; `null` is "All time". */
export function toHistoryRange(timeFrame: Time.PeriodLike | null): HistoryRange {
  if (timeFrame === null) return 'all'
  const seconds = Time.s.fromPeriod(timeFrame)
  if (seconds <= Time.s.fromPeriod('w')) return '1w'
  if (seconds <= Time.s.fromPeriod('mo')) return '1m'
  return '3m'
}

/**
 * True when a USD series has a non-zero amount but none of those amounts is valued, so the chart
 * shows its error. Zero amounts are skipped: they are worth $0 whatever the price.
 */
export function isUnpricedSeries(
  points: readonly { totalAssets: string | null; totalAssetsUsd: number | null }[]
): boolean {
  const amounts = points.filter(({ totalAssets }) => totalAssets !== '0')
  return amounts.length > 0 && amounts.every(({ totalAssetsUsd }) => totalAssetsUsd === null)
}

/** `isUnpricedSeries` for a position history, whose points carry `assets` and `assetsUsd`. */
export function isUnpricedPositionHistory(
  points: readonly { assets: string | null; assetsUsd: number | null }[]
): boolean {
  return isUnpricedSeries(
    points.map(({ assets, assetsUsd }) => ({ totalAssets: assets, totalAssetsUsd: assetsUsd }))
  )
}
